// Creating the records that always come as a set.

import { nextStep, STEP, type PipelineCondition, type PipelineStage, type PipelineStep } from '@zerogap/engine';
import { fetchRows, insertRow, insertRows, type Row } from './api';
import { today } from './format';

function addDays(date: string, days: number): string {
  const d = new Date(`${date}T12:00:00`);
  d.setDate(d.getDate() + days);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

/** A job starts with its workflow dates laid out from today, one base build, and the items its type starts every job with. */
export async function createJob(opportunityId: string, scopeName: string, jobTypeId: string | null, baseBuildName = 'Base'): Promise<Row> {
  // the four opportunity-level steps retired into the pipeline (2026-10-19) are no job's step: a job starts on the first live one
  const steps = (await fetchRows('workflow_step', { order: ['sort'] })).filter((s) => !s.retired_at);
  const job = await insertRow('job', {
    opportunity_id: opportunityId,
    scope_name: scopeName,
    job_type_id: jobTypeId,
    stage_id: steps[0]?.id ?? null,
  });
  const start = today();
  await insertRows(
    'job_step',
    steps.map((s) => ({
      job_id: job.id,
      workflow_step_id: s.id,
      target_date: s.target_offset_days === null ? null : addDays(start, s.target_offset_days),
      actual_date: s.sort === steps[0]?.sort ? start : null,
    })),
  );
  await insertRow('build', { job_id: job.id, name: baseBuildName, is_base: true, published: true, sort: 1 });
  if (jobTypeId) await addStartingItems(job.id, jobTypeId, []);
  return job;
}

/**
 * The catalog items a job type starts every job with (job_type_catalog_item.starts_on_job; Spencer, 2026-10-15:
 * "The customer care kit is just an item that I add on to all orders."). One job item each, numbered the way
 * + Item numbers them (prefix from the name, next free number), skipping any catalog item the job already has.
 * Returns the job items added.
 */
export async function addStartingItems(jobId: string, jobTypeId: string, existing: Row[]): Promise<Row[]> {
  const flagged = await fetchRows('job_type_catalog_item', { eq: { job_type_id: jobTypeId, starts_on_job: true } });
  const wanted = flagged.map((f) => f.catalog_item_id as string).filter((id) => !existing.some((i) => i.catalog_item_id === id));
  if (wanted.length === 0) return [];
  const cats = (await fetchRows('catalog_item', { in: { id: wanted } })).filter((c) => !c.retired).sort((a, b) => String(a.name).localeCompare(String(b.name)));
  const numbers = existing.map((i) => String(i.number ?? ''));
  let sort = Math.max(0, ...existing.map((i) => Number(i.sort ?? 0)));
  const rows: Row[] = [];
  for (const c of cats) {
    const number = nextNumber(numbers, prefixFor(String(c.name)));
    numbers.push(number);
    rows.push({ job_id: jobId, number, name: c.name, catalog_item_id: c.id, sort: ++sort });
  }
  return insertRows('job_item', rows);
}

/** A new opportunity starts on the pipeline's first working stage, at its first applying step, with the start on its log (2026-10-19). */
export async function createOpportunity(name: string, contactId: string | null): Promise<Row> {
  const stages = (await fetchRows('pipeline_stage', { order: ['sort'] })) as PipelineStage[];
  const steps = (await fetchRows('pipeline_step', { order: ['sort'] })) as PipelineStep[];
  const [cond] = await fetchRows('setting', { eq: { key: 'pipeline_conditions' } });
  const first = stages.find((s) => s.kind === 'working') ?? null;
  const start = first ? nextStep(steps, stages, (cond?.value ?? []) as PipelineCondition[], { pipeline_stage_id: first.id }, null) : null;
  const opp = await insertRow('opportunity', { name, primary_contact_id: contactId, pipeline_stage_id: first?.id ?? null, current_step_id: start?.step.id ?? null, current_step_since: new Date().toISOString() });
  if (contactId) await insertRow('opportunity_contact', { opportunity_id: opp.id, contact_id: contactId, role: 'Client' });
  await insertRow('activity', { opportunity_id: opp.id, kind: 'stage', body: `Captured${first ? ` · ${first.name}` : ''}${start ? ` · ${start.step.name}` : ''}`, source: 'app' });
  return opp;
}

/**
 * What changes on an opportunity when it moves to a step. The step whose key is `won` is
 * where it is won, whatever that step is named or wherever it sits in the order.
 */
export function stagePatch(opp: Row, step: Row): Row {
  const won = step.key === STEP.won && opp.outcome !== 'won';
  return { stage_id: step.id, ...(won ? { outcome: 'won', outcome_at: new Date().toISOString() } : {}) };
}

/** The next free item number with a prefix: B-01, B-02 … */
export function nextNumber(existing: string[], prefix: string): string {
  let n = 1;
  const taken = new Set(existing.map((e) => e.toUpperCase()));
  while (taken.has(`${prefix}-${String(n).padStart(2, '0')}`.toUpperCase())) n++;
  return `${prefix}-${String(n).padStart(2, '0')}`;
}

export function prefixFor(name: string): string {
  const letters = name.replace(/[^A-Za-z ]/g, '').trim().split(/\s+/).filter((w) => !/^(in|house|with|and|the|of)$/i.test(w));
  return (letters.at(-1)?.[0] ?? 'X').toUpperCase();
}
